import { FiChevronDown, FiLoader, FiXCircle, FiChevronUp, FiActivity, FiCheckCircle, FiX } from 'react-icons/fi';
import React, { useState } from 'react';
import { useGeneration } from '../../context/GenerationContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';


const GlobalGenerationOverlay = () => {
    const { activeTasks, completedTasks, clearTask, cancelTask } = useGeneration();
    const [isOpen, setIsOpen] = useState(false);

    const hasActive = activeTasks.length > 0;
    const hasCompleted = completedTasks.length > 0;
    const totalTasks = activeTasks.length + completedTasks.length;

    if (totalTasks === 0) return null;

    return (
        <div className="fixed bottom-6 left-6 z-[200] flex flex-col items-start">
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="mb-4 w-80 bg-white dark:bg-charcoal-800 rounded-xl shadow-2xl border border-charcoal-200 dark:border-charcoal-700 overflow-hidden flex flex-col"
                    >
                        <div className="px-4 py-3 border-b border-charcoal-100 dark:border-charcoal-700 flex justify-between items-center bg-charcoal-50 dark:bg-charcoal-800/50">
                            <h3 className="font-semibold text-charcoal-900 dark:text-white flex items-center gap-2">
                                <SafeIcon icon={FiActivity} className="text-blue-500" />
                                Background Tasks
                            </h3>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-200 transition-colors"
                            >
                                <SafeIcon icon={FiX} />
                            </button>
                        </div>
                        <div className="max-h-80 overflow-y-auto p-2 space-y-2">
                            {/* Active Tasks */}
                            {activeTasks.map(task => (
                                <div key={task.id} className="flex items-center gap-3 p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/30 group">
                                    <SafeIcon icon={FiLoader} className="text-blue-500 animate-spin flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-charcoal-900 dark:text-white truncate">{task.title}</p>
                                        <p className="text-xs text-charcoal-500 dark:text-charcoal-400">Generating...</p>
                                    </div>
                                    <button
                                        onClick={() => cancelTask(task.id)}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-all flex-shrink-0"
                                        title="Cancel generation"
                                        aria-label="Cancel task"
                                    >
                                        <SafeIcon icon={FiX} />
                                    </button>
                                </div>
                            ))}

                            {/* Completed Tasks */}
                            {completedTasks.map(task => (
                                <div key={task.id} className="flex items-center gap-3 p-3 bg-white dark:bg-charcoal-800 rounded-lg border border-charcoal-100 dark:border-charcoal-700 group">
                                    {task.status === 'success' ? (
                                        <SafeIcon icon={FiCheckCircle} className="text-green-500 flex-shrink-0" />
                                    ) : (
                                        <SafeIcon icon={FiXCircle} className="text-red-500 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-charcoal-900 dark:text-white truncate">{task.title}</p>
                                        <p className={`text-xs ${task.status === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400 truncate'}`}>
                                            {task.status === 'success' ? 'Completed' : task.error}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => clearTask(task.id)}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 text-charcoal-400 hover:text-charcoal-600 hover:bg-charcoal-100 dark:hover:bg-charcoal-700 rounded-md transition-all"
                                        aria-label="Dismiss task"
                                    >
                                        <SafeIcon icon={FiX} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-3 rounded-full shadow-lg border transition-all duration-300 hover:scale-105 ${
                    hasActive
                        ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-500 shadow-blue-500/25'
                        : 'bg-white dark:bg-charcoal-800 text-charcoal-700 dark:text-charcoal-200 border-charcoal-200 dark:border-charcoal-700 hover:bg-charcoal-50 dark:hover:bg-charcoal-700'
                }`}
            >
                {hasActive ? (
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                ) : (
                    <SafeIcon icon={hasCompleted && !isOpen ? FiCheckCircle : FiActivity} className={hasCompleted && !isOpen ? 'text-green-500' : ''} />
                )}
                <span className="font-medium text-sm">
                    {hasActive
                        ? `${activeTasks.length} Active Task${activeTasks.length !== 1 ? 's' : ''}`
                        : `${completedTasks.length} Completed Task${completedTasks.length !== 1 ? 's' : ''}`
                    }
                </span>
                <SafeIcon icon={isOpen ? FiChevronDown : FiChevronUp} className="ml-1 opacity-70" />
            </button>
        </div>
    );
};

export default GlobalGenerationOverlay;
